import { EStatus, IBody, IMutableWidgetsMap, IStateSession, TWAction, TFetchJson, TGetBody, TInitPayerOptions } from '@libs/types';
import { getDeviceId, getLocale } from '@libs/utils';
import { EntityId } from '@reduxjs/toolkit';
import {
    clearEmbeddedPlayerSession,
    getEmbeddedPlayerSession,
    setEmbeddedPlayerSession,
    AccessDeniedError,
    CFAccessDeniedError,
    fetchWithDelay,
    getOsDetails,
    getScreenPrint,
    AUTH,
    IS_SUPPORT_MULTI_TABS,
    getActionData,
    isCanDoRetry,
    hasScenario,
    hasScenarioWidgets,
} from '@libs/network-utils';

declare const VERSION: string;

export const getSessionObject = async (isRequestInteraction: boolean, playerId?: string, initPayerOptions?: TInitPayerOptions) => {
    const deviceId = getDeviceId();
    const osDetails = await getOsDetails();
    const isMatchesDark = window.matchMedia('(prefers-color-scheme: dark)')?.matches;
    const platform = 'Web';
    const session: IStateSession = {
        environment: {
            realm: platform,
            locale: getLocale(),
            data: {
                userAgent: navigator?.userAgent,
                deviceTheme: isMatchesDark ? 'dark' : 'light',
                operatingSystem: `${osDetails.name}${osDetails.version ? ' ' + osDetails.version : ''}`,
                id: `${platform.toUpperCase()}-${deviceId}`,
                deviceId: deviceId,
                authApp: `${platform} ${VERSION}`,
                authSource: platform,
                screenHeight: window?.innerHeight?.toString(),
                screenWidth: window?.innerWidth?.toString(),
                browserTimeZone: Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone,
                browserTimeZoneOffset: new Date().getTimezoneOffset?.().toString?.(),
                colorDepth: `${window?.screen?.colorDepth}`,
                screenPrint: getScreenPrint(),
            },
        },
    };
    if (osDetails?.architecture) {
        session.environment.data.model = osDetails?.architecture;
    }
    if (playerId) {
        const embeddedSession = getEmbeddedPlayerSession(playerId, isRequestInteraction);
        if (embeddedSession?.auth) {
            session.auth = embeddedSession.auth;
        }
        if (embeddedSession?.isFillParent) {
            session.parent = { ...session };
        }
    } else {
        const authSessionStorage = sessionStorage?.getItem(AUTH);
        const authLocalStorage = localStorage?.getItem(AUTH);

        if (authSessionStorage) {
            session.auth = JSON.parse(authSessionStorage);
        } else if (authLocalStorage) {
            if (IS_SUPPORT_MULTI_TABS) {
                session.parent = { ...session, auth: JSON.parse(authLocalStorage) };
            } else {
                session.auth = JSON.parse(authLocalStorage);
            }
        }
    }
    if (isRequestInteraction && initPayerOptions) {
        if (initPayerOptions.externalData && Object.keys(initPayerOptions.externalData).length > 0) {
            session.environment.data.externalData = initPayerOptions.externalData;
        }
        if (initPayerOptions.requestUri) {
            session.environment.requestUri = initPayerOptions.requestUri;
        }
    }
    return session;
};

export const getBody: TGetBody = async <TData, TOptions>(
    action?: TWAction | undefined,
    currentPageId?: string,
    mutableWidgetsMap?: IMutableWidgetsMap<TData, TOptions>,
    playerId?: string,
    initPayerOptions?: TInitPayerOptions,
) => {
    if (action?.clearSession) {
        // localStorage?.removeItem(LAST_ACTION);
        if (playerId) {
            clearEmbeddedPlayerSession(playerId);
        } else {
            sessionStorage?.removeItem(AUTH);
            localStorage?.removeItem(AUTH);
        }
    }
    const body: IBody = { session: await getSessionObject(!action, playerId, initPayerOptions) };
    if (action) {
        const pageId: EntityId = currentPageId ?? '';
        const data: ReturnType<typeof getActionData> = getActionData(Object.values(mutableWidgetsMap ?? {}));
        body.action = { ...action, data, page: pageId };
        // if (IS_SUPPORT_MULTI_TABS) {
        //     localStorage.setItem(LAST_ACTION, JSON.stringify(body.action));
        // }
    }
    return body;
};

export const fetchJson: TFetchJson = async <TData, TOptions>(
    action: TWAction | undefined,
    isShowLoader: boolean,
    widgetUid: string | undefined,
    currentPageId: string,
    mutableWidgetsMap: IMutableWidgetsMap<TData, TOptions>,
    setPlayerState: (status: EStatus) => void,
    keepalive?: boolean,
    playerId?: string,
    initPayerOptions?: TInitPayerOptions,
    attempt = 1,
) => {
    // const actionToCall = getAction(action);
    try {
        const body: IBody = await getBody(action, currentPageId, mutableWidgetsMap, playerId, initPayerOptions);

        const json = await fetchWithDelay(body, action, isShowLoader, widgetUid, setPlayerState, keepalive, playerId);
        if (isCanDoRetry(action, attempt)) {
            if (!hasScenario(json)) {
                sessionStorage?.removeItem(AUTH);
                localStorage?.removeItem(AUTH);
            }
            if (!hasScenarioWidgets(json)) {
                return fetchJson(
                    action,
                    isShowLoader,
                    widgetUid,
                    currentPageId,
                    mutableWidgetsMap,
                    setPlayerState,
                    keepalive,
                    playerId,
                    initPayerOptions,
                    ++attempt,
                );
            }
        }
        if (hasScenarioWidgets(json)) {
            if (json?.session?.auth) {
                if (playerId) {
                    setEmbeddedPlayerSession(playerId, { auth: json.session.auth });
                } else {
                    if (IS_SUPPORT_MULTI_TABS) {
                        sessionStorage.setItem(AUTH, JSON.stringify(json.session.auth));
                    }
                    localStorage.setItem(AUTH, JSON.stringify(json.session.auth));
                }
            }
            return json;
        }
    } catch (e) {
        const idAccessDeniedError = e instanceof AccessDeniedError;
        const isCFAccessDeniedError = e instanceof CFAccessDeniedError;
        if (isCFAccessDeniedError) {
            console.error('fetchJson: CFAccessDeniedError', e);
            throw e;
        } else if (!idAccessDeniedError && isCanDoRetry(action, attempt)) {
            return fetchJson(
                action,
                isShowLoader,
                widgetUid,
                currentPageId,
                mutableWidgetsMap,
                setPlayerState,
                keepalive,
                playerId,
                initPayerOptions,
                ++attempt,
            );
        } else {
            console.error('fetchJson', e);
            throw e;
        }
    }
    throw new Error('fetchJson: has no scenario widgets.');
};
